/**
 * Chữ của lựa chọn «Thời gian nghỉ» (ô thời lượng của đơn nghỉ) ở trình dựng mẫu + lúc hiển thị.
 *
 * ══ BỐI CẢNH ══
 * Máy chủ tính số ngày của đơn bằng kernel `leaveBalancePolicy` (SINH từ cnv-server-shared, ĐỪNG sửa tệp
 * đó ở đây): đọc NHÃN lựa chọn trước, không hiểu thì đọc MÃ (`o.value`). Bộ đọc của kernel chỉ hiểu chữ
 * TIẾNG VIỆT («Nửa ngày», «Buổi sáng», «1,5 ngày», «Cả ngày»…) + đúng một cụm «full day».
 * Trước 12/10/2026 trình dựng gọi `formatLeaveDurationLabel` lúc rời ô và GHI ĐÈ nhãn admin vừa gõ bằng
 * dạng chuẩn tiếng Việt — ở mọi ngôn ngữ: admin Site Connect gõ «full day» thành «Cả ngày», «0.5» thành
 * «Nửa ngày», và nhân viên nói tiếng Anh nhìn thấy đúng chữ Việt đó khi chọn.
 *
 * ══ LUẬT MỚI ══
 * • App đang tiếng Việt (Work, Core, người dùng vi): Y NHƯ CŨ — nhãn đưa về dạng chuẩn tiếng Việt, đó là
 *   ngôn ngữ của chính admin.
 * • Ngôn ngữ khác: KHÔNG đụng chữ admin gõ (chỉ gọn khoảng trắng). Dạng chuẩn tiếng Việt — thứ máy chủ đọc
 *   được — nằm ở MÃ của lựa chọn (chỉ gán cho lựa chọn MỚI, mã đã có thì giữ vì đơn cũ + điều kiện hiện/ẩn
 *   trỏ vào nó). Máy chủ: nhãn «Half day» không hiểu ⇒ đọc mã «Nửa ngày» ⇒ 0,5. Để admin gõ được tiếng
 *   Anh/Pháp, `toKernelDurationText` dịch vài cụm thông dụng sang chữ kernel hiểu TRƯỚC khi chuẩn hoá.
 * • Hiển thị: nhãn ĐÚNG dạng chuẩn tiếng Việt (mẫu cũ đã bị ghi đè, hoặc mẫu dựng bằng tiếng Việt) được dịch
 *   theo ngôn ngữ người xem (`leaveDurationOptionLabel`). Chữ khác của admin hiện nguyên văn.
 */
import { msg } from '@lingui/core/macro';
import { i18n, getLocale } from '../../../i18n/runtime.js';
import { fmtNumber } from '../../../i18n/format.js';
import { findLeaveDurationField, formatLeaveDurationLabel, parseLeaveDuration } from '../../../utils/leaveBalancePolicy';

const isVi = () => getLocale() === 'vi';

// Chữ thường, bỏ dấu, «-» / «_» thành khoảng trắng, gộp khoảng trắng.
function fold(s) {
  return String(s == null ? '' : s)
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
}

// i18n-data: cụm EN/FR → chữ tiếng Việt mà bộ đọc của kernel hiểu (không phải chữ hiển thị).
const ALIASES = {
  'half day': 'nửa ngày', 'half a day': 'nửa ngày', half: 'nửa ngày',
  'demi journee': 'nửa ngày', 'demie journee': 'nửa ngày', 'une demi journee': 'nửa ngày',
  morning: 'buổi sáng', am: 'buổi sáng', 'a.m.': 'buổi sáng', 'morning half': 'buổi sáng',
  matin: 'buổi sáng', matinee: 'buổi sáng',
  afternoon: 'buổi chiều', pm: 'buổi chiều', 'p.m.': 'buổi chiều', 'afternoon half': 'buổi chiều',
  'apres midi': 'buổi chiều',
  'full day': 'cả ngày', 'whole day': 'cả ngày', 'all day': 'cả ngày', 'entire day': 'cả ngày',
  'journee complete': 'cả ngày', 'journee entiere': 'cả ngày', 'toute la journee': 'cả ngày',
  'jour complet': 'cả ngày', journee: 'cả ngày',
  'quarter day': '1/4 ngày', 'a quarter day': '1/4 ngày', 'quarter of a day': '1/4 ngày',
  'quart de journee': '1/4 ngày',
};
const DAY = '(?:days?|d|jours?|j|journees?)';

/** Chữ admin gõ bằng tiếng Anh/Pháp → chữ tiếng Việt bộ đọc của kernel hiểu; không nhận ra ⇒ trả nguyên. */
export function toKernelDurationText(text) {
  const raw = String(text == null ? '' : text);
  const s = fold(raw);
  if (!s) return raw;
  if (ALIASES[s]) return ALIASES[s];
  let m = new RegExp(`^(\\d+(?:[.,]\\d+)?|1/2|1/4) ?${DAY}$`).exec(s);
  if (m) return `${m[1]} ngày`;
  // «1 and a half days» · «1 day and a half» · «1 jour et demi»
  m = new RegExp(`^(\\d+) ?(?:${DAY} )?(?:and a half|et demie?|½)(?: ${DAY})?$`).exec(s);
  if (m) return `${m[1]} ngày rưỡi`;
  return raw;
}

/** Dạng chuẩn (tiếng Việt, kernel đọc được) của chữ admin gõ — theo ngôn ngữ đang hiển thị. */
function canonicalOf(text) {
  return formatLeaveDurationLabel(isVi() ? text : toKernelDurationText(text));
}

/**
 * {label, value} sẽ LƯU cho lựa chọn thứ `i` khi admin rời ô — xem «LUẬT MỚI» đầu tệp.
 * `options`: cả danh sách (để mã mới không trùng mã lựa chọn khác).
 */
export function planLeaveDurationOption(options, i) {
  const o = options[i] || {};
  const typed = String(o.label == null ? '' : o.label).replace(/\s+/g, ' ').trim();
  const canonical = canonicalOf(typed);
  const label = isVi() ? canonical : typed;
  // Mã đã có thì GIỮ (đơn cũ + điều kiện hiện/ẩn của ô khác trỏ vào nó) — máy chủ đọc nhãn trước.
  let value = o.value;
  if (!value) {
    const taken = new Set(options.filter((_, j) => j !== i).map((x) => x.value));
    value = canonical;
    for (let k = options.length; !value || taken.has(value); k += 1) value = `opt${k}`;
  }
  return { label, value };
}

/**
 * Hai con số cho dòng gợi ý dưới lựa chọn:
 *   days     — số ngày MÁY CHỦ sẽ tính với lựa chọn như sẽ lưu (nhãn trước, mã sau); null = không đọc được.
 *   meantDays — số ngày theo CHỮ admin vừa gõ; khác `days` khi lựa chọn cũ giữ mã cũ mà nhãn đã đổi nghĩa.
 */
export function leaveDurationOptionDays(options, i) {
  const planned = planLeaveDurationOption(options, i);
  const days = parseLeaveDuration(planned.label) ?? parseLeaveDuration(planned.value);
  const meantDays = parseLeaveDuration(canonicalOf(planned.label));
  return { days, meantDays };
}

// Nhãn hiển thị của các dạng chuẩn tiếng Việt (đúng tập `formatLeaveDurationLabel` sinh ra).
const CANONICAL_LABELS = {
  'Buổi sáng': msg({ message: 'Buổi sáng', context: 'thời lượng nghỉ' }),
  'Buổi chiều': msg({ message: 'Buổi chiều', context: 'thời lượng nghỉ' }),
  'Nửa ngày': msg({ message: 'Nửa ngày', context: 'thời lượng nghỉ' }),
  'Cả ngày': msg({ message: 'Cả ngày', context: 'thời lượng nghỉ' }),
  '1/4 ngày': msg({ message: '1/4 ngày', context: 'thời lượng nghỉ' }),
};

/**
 * Nhãn lựa chọn thời lượng để HIỂN THỊ. Tiếng Việt ⇒ nguyên văn. Ngôn ngữ khác: nhãn đúng dạng chuẩn tiếng
 * Việt («Nửa ngày», «1,5 ngày»…) ⇒ bản dịch; còn lại (chữ admin tự gõ) ⇒ nguyên văn.
 */
export function leaveDurationOptionLabel(label) {
  if (isVi() || typeof label !== 'string' || !label) return label;
  if (CANONICAL_LABELS[label]) return i18n._(CANONICAL_LABELS[label]);
  const m = /^(\d+(?:,\d+)?) ngày$/.exec(label);
  if (!m || formatLeaveDurationLabel(label) !== label) return label;
  const n = Number(m[1].replace(',', '.'));
  if (n === 1) return i18n._(msg({ message: '1 ngày', context: 'thời lượng nghỉ' }));
  const days = fmtNumber(n, { maxD: 2 });
  return i18n._(msg({ message: `${days} ngày`, context: 'thời lượng nghỉ' }));
}

/** `field` có phải ô thời lượng nghỉ (đúng ô máy chủ đọc) không. Thiếu `formFields` ⇒ xét riêng ô đó. */
export function isLeaveDurationField(field, formFields) {
  if (!field) return false;
  const list = Array.isArray(formFields) && formFields.length ? formFields : [field];
  return findLeaveDurationField(list)?.id === field.id;
}

/** Hàm lấy nhãn hiển thị cho lựa chọn của `field` — chỉ ô thời lượng nghỉ mới qua `leaveDurationOptionLabel`. */
export function optionLabelOf(field, formFields) {
  return isLeaveDurationField(field, formFields)
    ? (o) => leaveDurationOptionLabel(o?.label)
    : (o) => o?.label;
}
